/**
 * schoolAssessmentFormatter — pure, presentational derivations for school assessments.
 *
 * Lives in `ports/primary/ui/formatters/` (sibling of `ui/types/`), outside `design-system/`, so DS components stay
 * business-agnostic while importing only pure, Storybook-safe helpers. No `@data` / store / service deps.
 *
 * The overall preparation progress and the assessment state are NOT stored on `UISchoolAssessment` —
 * they are derived from `chaptersProgress` here (the single source), mirroring the legacy backend
 * (`preparation_progress` = average of per-chapter progress; `preparation_status` = thresholds on it).
 *
 * Create-draft helpers live in `createSchoolAssessmentFormatter.ts`.
 */
import { type UISchoolAssessment } from "../types/schoolAssessment"

type ChaptersProgress = UISchoolAssessment["chaptersProgress"]

/** Assessment preparation state — mirrors the legacy `preparation_status` enum. */
type AssessmentState = "notStarted" | "inProgress" | "ready"

/** Overall preparation progress (0–100): the average of per-chapter progress. `0` when empty. */
export function preparationProgress(chaptersProgress: ChaptersProgress): number {
    if (chaptersProgress.length === 0) return 0
    const sum = chaptersProgress.reduce((acc, item) => acc + item.progress, 0)
    return Math.round(sum / chaptersProgress.length)
}

/**
 * Assessment state derived from the preparation progress (legacy backend thresholds):
 * `≤ 30 → notStarted`, `≤ 70 → inProgress`, else `ready`.
 */
export function assessmentState(chaptersProgress: ChaptersProgress): AssessmentState {
    const progress = preparationProgress(chaptersProgress)
    if (progress <= 30) return "notStarted"
    if (progress <= 70) return "inProgress"
    return "ready"
}
